import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./dashboard.css";
import companyIcon from "../../Asserts/Images/total_company.png";
import certificationIcon from "../../Asserts/Images/certified_icon.png";
import auditIcon from "../../Asserts/Images/audit_img.png";
import Instance from "../../AxiosConfig";
import { message, Select, DatePicker } from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const DashboardCards = () => {
  const navigate = useNavigate();

  // ===== Dashboard Stats =====
  const [companyStats, setCompanyStats] = useState(null);
  const [proformaStats, setProformaStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [gstStats, setGstStats] = useState(null);
  const [tdsStats, setTdsStats] = useState(null);

  const [taxRange, setTaxRange] = useState("all");
  const [customDates, setCustomDates] = useState({
    from: null,
    to: null,
  });

  // Dynamic pending payment cards
  const [pendingPayments, setPendingPayments] = useState([]);

  // Recent data
  const [recentRecords, setRecentRecords] = useState(null);

  // ===== Navigation Handlers =====
  const goToCompanies = () => navigate("/companies");
  const goToProforma = () => navigate("/proforma-invoice");
  const goToInvoices = () => navigate("/tax-invoice");
  const goToGST = () => navigate("/gst-reports-table");
  const goToTDS = () => navigate("/tds-reports-table");
  const goToPendingPayments = () => navigate("/pending-payments-list");

  // ===== API Call =====
  const fetchDashboardData = async () => {
    try {
      const params = {};

      if (taxRange !== "all") {
        params.taxRange = taxRange;

        if (taxRange === "custom") {
          params.from = customDates.from;
          params.to = customDates.to;
        }
      }

      const response = await Instance.get("dashboard/data", {
        params,
      });

      if (response.status === 200) {
        const { totals, recent } = response.data;

        setRecentRecords(recent);

        setCompanyStats(totals.totalCompanies);
        setProformaStats(totals.totalProforma);
        setInvoiceStats(totals.totalInvoices);

        // Only these values change with the filter
        setGstStats(totals.totalGST);
        setTdsStats(totals.totalTDS);

        setPendingPayments(totals.totalPendingPayment || []);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load dashboard data");
    }
  };

  useEffect(() => {
    if (
      taxRange !== "custom" ||
      (customDates.from && customDates.to)
    ) {
      fetchDashboardData();
    }
  }, [taxRange, customDates]);

  const handleRangeChange = (value) => {
    setTaxRange(value);

    if (value !== "custom") {
      setCustomDates({
        from: null,
        to: null,
      });
    }
  };

  const handleCustomDateChange = (dates) => {
    if (!dates) {
      setCustomDates({
        from: null,
        to: null,
      });
      return;
    }

    setCustomDates({
      from: dates[0].format("YYYY-MM-DD"),
      to: dates[1].format("YYYY-MM-DD"),
    });
  };

  return (
    <div className="card-main">
      <div className="dashboard-cards">

        {/* Total Companies */}
        <div className="dashboard-card" onClick={goToCompanies}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="card-content">
              <h5>Total Companies</h5>
              <h2>{companyStats ?? "Loading..."}</h2>
            </div>
            <div className="card-icon-one-bg">
              <img src={companyIcon} alt="" className="card-icon" />
            </div>
          </div>
          <h6 className="card-h6">
            Latest Company:
            <span style={{ color: "blue" }}>
              {" "}
              {recentRecords?.companies?.[0]?.companyName ?? "N/A"}
            </span>
          </h6>
        </div>

        {/* Proforma */}
        <div className="dashboard-card" onClick={goToProforma}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="card-content">
              <h5>Total Proforma</h5>
              <h2>{proformaStats ?? "Loading..."}</h2>
            </div>
            <div className="card-icon-one-bg certification-icon">
              <img src={certificationIcon} alt="" className="card-icon" />
            </div>
          </div>
          <h6 className="card-h6">
            Latest Proforma:
            <span style={{ color: "blue" }}>
              {" "}
              {recentRecords?.proforma?.[0]?.invoiceNo ?? "N/A"}
            </span>
          </h6>
        </div>

        {/* Invoices */}
        <div className="dashboard-card" onClick={goToInvoices}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="card-content">
              <h5>Total Invoices</h5>
              <h2>{invoiceStats ?? "Loading..."}</h2>
            </div>
            <div className="card-icon-one-bg certification-icon">
              <img src={certificationIcon} alt="" className="card-icon" />
            </div>
          </div>
          <h6 className="card-h6">
            Latest Invoice:
            <span style={{ color: "blue" }}>
              {" "}
              {recentRecords?.invoices?.[0]?.invoiceNo ?? "N/A"}
            </span>
          </h6>
        </div>

        {/* ===== Dynamic Pending Payment Cards ===== */}
        {pendingPayments.map((item, index) => (
          <div
            className="dashboard-card"
            key={index}
            onClick={goToPendingPayments}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="card-content">
                <h5>Pending Payment ({item._id})</h5>
                <h2>{item.totalPendingAmtINR}</h2>
              </div>
              <div className="card-icon-one-bg surveliance-icon-bg">
                <img src={auditIcon} alt="" className="card-icon" />
              </div>
            </div>
            <h6 className="card-h6">
              Currency:
              <span style={{ color: "blue" }}> {item._id}</span>
            </h6>
          </div>
        ))}

      </div>

      {/* ================= Tax Summary ================= */}

      <div style={{ marginTop: 35 }}>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 15,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <h4 style={{ margin: 0 }}>
            GST & TDS Summary
          </h4>

          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <Select
              style={{ width: 180 }}
              value={taxRange}
              onChange={handleRangeChange}
              options={[
                { value: "all", label: "All Time" },
                { value: "1m", label: "Last Month" },
                { value: "3m", label: "Last 3 Months" },
                { value: "6m", label: "Last 6 Months" },
                { value: "1y", label: "Last Year" },
                { value: "custom", label: "Custom Range" },
              ]}
            />

            {taxRange === "custom" && (
              <RangePicker
                value={
                  customDates.from && customDates.to
                    ? [
                      dayjs(customDates.from),
                      dayjs(customDates.to),
                    ]
                    : null
                }
                onChange={handleCustomDateChange}
              />
            )}
          </div>
        </div>

        <div className="dashboard-cards">
          {/* GST */}
          <div className="dashboard-card" onClick={goToGST}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="card-content">
                <h5>Total GST</h5>
                <h2>{gstStats ?? "Loading..."}</h2>
              </div>
              <div className="card-icon-one-bg certification-icon">
                <img src={certificationIcon} alt="" className="card-icon" />
              </div>
            </div>
            <h6 className="card-h6">
              Latest GST:
              <span style={{ color: "blue" }}>
                {" "}
                {recentRecords?.invoicewithINR?.[0]?.terms?.[0]?.gstAmount ?? "N/A"}
              </span>
            </h6>
          </div>

          {/* TDS */}
          <div className="dashboard-card" onClick={goToTDS}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="card-content">
                <h5>Total TDS</h5>
                <h2>{tdsStats ?? "Loading..."}</h2>
              </div>
              <div className="card-icon-one-bg surveliance-icon-bg">
                <img src={auditIcon} alt="" className="card-icon" />
              </div>
            </div>
            <h6 className="card-h6">
              Latest TDS:
              <span style={{ color: "blue" }}>
                {" "}
                {recentRecords?.invoicewithINR?.[0]?.terms?.[0]?.TDSAmmount ?? "N/A"}
              </span>
            </h6>
          </div>


        </div>

      </div>
    </div>
  );
};

export default DashboardCards;
